import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Loader from '../../../loaders';
import { FiltersConverter, SortsConverter } from '../../../../helpers';
import api from "../../../../services/api";
import { Card, CardBody, Col, Container, Row } from 'reactstrap';
import { TooltipButton } from '../../../inputs';
import { AgGridReact } from 'ag-grid-react';
import qs from "qs";
import { IndeterminateCheckbox } from '../../../react-table/controls';

const pageSize = 50;
const apiUrl = "employeechangeconditionv2";

export default function ECCApprovalList() {
    const [showActiveApproval, setShowActiveApproval] = useState(false);
    const [showAll, setShowAll] = useState(false);
    const loadingOverlayComponent = useMemo(() => Loader, []);
    const gridRef = useRef();

    const defaultColDef = useMemo(() => ({
        sortable: true,
        resizable: true,
        filter: true,
        editable: false,
        filter: 'agTextColumnFilter',
        filterParams: {
            buttons: ['reset'],
            debounceMs: 200,
            excelMode: 'mac',
        },
        floatingFilter: true,
        floatingFilterComponentParams: {
            suppressFilterButton: true,
        },
        suppressMenu: true,
    }), []);

    const getServerSideDatasource = useCallback(() => {
        return {
            getRows: (params) => {
                const { request } = params;
                const { filterModel, sortModel } = request;

                const currentFilters = FiltersConverter(filterModel);
                const currentSorts = SortsConverter(sortModel);

                const queryString = qs.stringify({ filters: currentFilters, sortBy: currentSorts, showActiveApproval, showAll }, { allowDots: true });
                const url = `/${apiUrl}/approval/${params.request.startRow ?? 0}/${pageSize}${params.requestId ? ("/" + params.requestId + "/" + params.action) : ""}${queryString ? `?${queryString}` : ""}`;

                api.get(url)
                    .then((response) => {
                        // console.log({response});
                        const successData = {
                            rowData: response.data.data,
                            rowCount: response.data.total,
                        };

                        params.success(successData);
                    })
                    .catch(error => {
                        params.fail();
                        console.error({error});
                    });
            },
        };
    }, [pageSize, showActiveApproval, showAll]);

    const onGridReady = useCallback((params) => {
        params.api.setServerSideDatasource(getServerSideDatasource());
        params.api.sizeColumnsToFit();
        window.addEventListener('resize', function () {
            setTimeout(function () {
                params.api.sizeColumnsToFit();
            });
        });
    }, [getServerSideDatasource]);

    useEffect(() => {
        if (!gridRef?.current?.api)
            return;
        
        const datasource = getServerSideDatasource();
        gridRef.current.api.setServerSideDatasource(datasource);
    }, [showAll, showActiveApproval]);

    const columnDefs = [
        {
            field: "employeeChangeConditionNumber",
            headerName: "ECC Number",
            sort: "asc",
            minWidth: 150,
        },
        {
            field: "candidateName",
            headerName: "Employee Name",
            minWidth: 150,
        },
        {
            field: "projectName",
            headerName: "Project",
            minWidth: 150,
        },
        {
            field: "recruitmentTypeName",
            headerName: "Proposed Recruitment Type",
            minWidth: 150,
        },
        {
            field: "employmentTypeName",
            headerName: "Proposed Employment Basis",
            minWidth: 150,
        },
        {
            field: "createdBy",
            headerName: "Requested By",
            minWidth: 150,
        },
        {
            field: "statusName",
            headerName: "Status",
            minWidth: 150,
        },
        {
            field: "approverName",
            headerName: "Next Approver",
            minWidth: 150,
        },
        {
            field: "actions",
            headerName: "",
            filter: false,
            maxWidth: 90,
            minWidth: 90,
            pinned: "right",
            cellRenderer: (row) => {
                const {data: rowData} = row;
                const canApprove = rowData.statusName === "Pending Approval";
                return (
                    <div key={`ecc_approval_action_${rowData.eccApprovalId}`} id={`ecc_approval_action_${rowData.eccApprovalId}`} className="text-center">
                        <TooltipButton
                            id={`ecc_approval_edit_${rowData.eccApprovalId}`}
                            title="Set Approval Status"
                            className="btn-icon"
                            color="default"
                            size="xss"
                            type="button"
                            onClick={() => window.open(`/eccapproval/${rowData.eccApprovalId}?eccId=${rowData.employeeChangeConditionId}`, '_blank')}
                        >
                            <i className={`fa fa-xs ${canApprove ? "fa-pencil-alt" : "fa-search"} pt-1`}></i>
                        </TooltipButton>
                    </div>
                );
            }
        },
    ];

    return (
        <>
            <Row>
                <Col sm={12} className="d-flex flex-wrap justify-content-end align-items-center">
                    <IndeterminateCheckbox
                        id="showActiveApproval"
                        type="checkbox"
                        labelClassName="job-order-control mr-3"
                        onChange={() => {
                            const newValue = !showAll;
                            setShowActiveApproval(newValue);

                            if (!newValue && showAll)
                                setShowAll(newValue);
                        }}
                        checked={showActiveApproval}
                        customTitle="Show All Active Approval"
                    />
                    <IndeterminateCheckbox
                        id="showAll"
                        type="checkbox"
                        labelClassName="job-order-control mr-3"
                        onChange={() => {
                            const newValue = !showAll;
                            setShowAll(newValue);

                            if (newValue && !showActiveApproval)
                                setShowActiveApproval(newValue);
                        }}
                        checked={showAll}
                        customTitle="Show All Approval History"
                    />
                </Col>
            </Row>
            <Row>
                <Col sm={12}>
                    <div className="ag-theme-alpine ag-grid" style={{ width: '100%', height: 'calc(100vh - 200px)' }}>
                        <AgGridReact
                            ref={gridRef}
                            columnDefs={columnDefs}
                            defaultColDef={defaultColDef}
                            loadingOverlayComponent={loadingOverlayComponent}
                            suppressColumnVirtualisation={false}
                            suppressRowVirtualisation={false}
                            debounceVerticalScrollbar={false}
                            rowBuffer={20}
                            headerHeight={30}
                            groupHeaderHeight={30}
                            rowHeight={30}
                            floatingFiltersHeight={30}
                            suppressDragLeaveHidesColumns={true}
                            suppressColumnMoveAnimation={true}
                            animateRows={false}
                            pagination={true}
                            paginationPageSize={pageSize}
                            cacheBlockSize={pageSize}
                            rowModelType={'serverSide'}
                            serverSideInfiniteScroll={true}
                            onGridReady={onGridReady}
                            rowClass={'ag-row-normal'}
                        />
                    </div>
                </Col>
            </Row>
        </>
    );
};